import {
    ProductDetailsDataInterface,
    ProductAttribute,
    ProductBeverage,
    ProductAssociations,
} from "../../../common/interfaces/ProductDetailsDataInterface";
import { ProductInterface } from "../../../common/interfaces/ProductInterface";
import { MetricValue } from "../../../common/interfaces/MetricValueInterface";
import { TranslationsInterface } from "../../../common/interfaces/TranslationsInterface";
import { getLocalizedText } from "../../../common/utils/getLocalizedText";
import { convertUnitToAbbreviation } from "../../../common/utils/convertUnitToAbbreviation";
import { convertProduct } from "../../../common/utils/productConverter";
import {
    DstAssociationCard,
    DstBeverageItem,
    DstProductDetailAttribute,
    DstProductDetailViewModel,
} from "../interfaces/ProductDetailViewTypes";

const ACCESSORIES_CATEGORY_ID = 139;

interface AttributeValueResult {
    value: string;
    is_bullet_points: boolean;
    items: string[];
}

const resolveAttributeValue = (
    item: ProductAttribute,
    identifier: string,
): AttributeValueResult | null => {
    // apiV2/digital-sales already resolves attribute values to display-ready
    // strings server-side (including bullet-list grouping), so these can be
    // used directly without the legacy per-type parsing below.
    if (item.is_bullet_points !== undefined || item.items !== undefined) {
        const value = typeof item.value === "string" ? item.value : "";
        const items = item.items ?? [];

        if (!value && items.length === 0) {
            return null;
        }

        return {
            value,
            is_bullet_points: Boolean(item.is_bullet_points),
            items,
        };
    }

    const rawValue = item.value as
        | string
        | number
        | boolean
        | Record<string, string | number | boolean | MetricValue>;

    if (item.type === "identifier") {
        return { value: identifier, is_bullet_points: false, items: [] };
    }

    if (
        item.type === "boolean" &&
        typeof rawValue === "object" &&
        rawValue &&
        Object.values(rawValue)[0] !== undefined
    ) {
        return {
            value: Object.values(rawValue)[0] ? "Yes" : "No",
            is_bullet_points: false,
            items: [],
        };
    }

    if (
        item.type === "number" &&
        typeof rawValue === "object" &&
        rawValue &&
        Object.values(rawValue)[0] !== undefined
    ) {
        return {
            value: Object.values(rawValue)[0].toString(),
            is_bullet_points: false,
            items: [],
        };
    }

    if (item.type === "text" && rawValue) {
        return {
            value: getLocalizedText(
                rawValue as unknown as TranslationsInterface,
            ),
            is_bullet_points: false,
            items: [],
        };
    }

    if (
        item.type === "metric" &&
        typeof rawValue === "object" &&
        rawValue &&
        Object.values(rawValue)[0]
    ) {
        const metricValue = Object.values(rawValue)[0] as MetricValue;
        const amount = metricValue.amount.replace(/\.0000$/, "");
        const unit = convertUnitToAbbreviation(metricValue.unit);
        return {
            value: `${amount} ${unit}`,
            is_bullet_points: false,
            items: [],
        };
    }

    if (
        typeof rawValue === "object" &&
        rawValue &&
        Object.values(rawValue).length > 0 &&
        !!Object.values(rawValue)[0] &&
        item.type !== "boolean" &&
        item.type !== "text" &&
        item.type !== "number" &&
        item.type !== "metric"
    ) {
        const values = Object.values(rawValue).map((value) =>
            getLocalizedText(value as unknown as TranslationsInterface),
        );
        if (values.length > 1) {
            return {
                value: "",
                is_bullet_points: true,
                items: values,
            };
        }
        return {
            value: values[0] ?? "",
            is_bullet_points: false,
            items: [],
        };
    }

    return null;
};

export const mapDstDetailAttribute = (
    item: ProductAttribute,
    identifier: string,
): DstProductDetailAttribute | null => {
    const resolved = resolveAttributeValue(item, identifier);
    if (!resolved || (!resolved.value && resolved.items.length === 0)) {
        return null;
    }

    return {
        code: item.code,
        label: getLocalizedText(item.label),
        value: resolved.value,
        is_bullet_points: resolved.is_bullet_points,
        items: resolved.items,
    };
};

export const mapDstBeverages = (
    beverages: ProductBeverage[] | undefined,
): DstBeverageItem[] =>
    (beverages ?? []).map((beverage) => ({
        code: beverage.code,
        label: beverage.label,
        iconUrl: beverage.icon_url,
    }));

export const mapDstProductToViewModel = (
    data: ProductDetailsDataInterface,
): DstProductDetailViewModel => ({
    id: data.id,
    identifier: data.identifier,
    label: getLocalizedText(data.label),
    description: data.description || null,
    dimensions: data.dimensions || null,
    productFamily: data.productFamily
        ? {
              code: data.productFamily.code,
              label: getLocalizedText(data.productFamily.label),
          }
        : null,
    categories: data.categories,
    productAttributes: (data.detailAttributes ?? [])
        .map((attribute) => mapDstDetailAttribute(attribute, data.identifier))
        .filter((attribute): attribute is DstProductDetailAttribute =>
            Boolean(attribute),
        ),
    beverages: mapDstBeverages(data.beverageItems),
});

export interface ProductSpecificationEntry {
    key: string;
    label: string;
    dimensionsValue?: string;
    attribute?: DstProductDetailAttribute;
}

export const buildDstSpecificationEntries = (
    data: DstProductDetailViewModel,
    dimensionsLabel: string,
): ProductSpecificationEntry[] => {
    const entries: ProductSpecificationEntry[] = data.productAttributes.map(
        (attribute) => ({
            key: attribute.code,
            label: attribute.label,
            attribute,
        }),
    );

    if (data.dimensions) {
        entries.push({
            key: "dimensions",
            label: dimensionsLabel,
            dimensionsValue: data.dimensions,
        });
    }

    return entries;
};

export const mapDstAssociationsForSliders = (
    productAssociations: ProductAssociations | null,
    productImages: Record<string, string>,
): {
    accessories: DstAssociationCard[];
    similarProducts: DstAssociationCard[];
} => {
    const mapProduct = (product: ProductInterface): DstAssociationCard => {
        const converted = convertProduct(product, productImages, "related");

        return {
            id: product.id,
            label: converted.title,
            identifier: product.identifier,
            image: converted.image ?? "",
            similarProductsSpecification:
                converted.similarProductsSpecification,
        };
    };

    const upSell = (productAssociations?.up_sell ?? []) as ProductInterface[];
    const previousModel = (productAssociations?.previous_model ??
        []) as ProductInterface[];

    return {
        accessories: upSell
            .filter((product) =>
                product.categories.includes(ACCESSORIES_CATEGORY_ID),
            )
            .map(mapProduct),
        similarProducts: previousModel
            .filter(
                (product) =>
                    !product.categories.includes(ACCESSORIES_CATEGORY_ID),
            )
            .map(mapProduct),
    };
};
